import React, { useCallback, useEffect, useState } from 'react';

import AdminDataTable from '../../components/admin/AdminDataTable';
import { formatAdminDateTime } from '../../components/admin/adminFormatting';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminStatCard from '../../components/admin/AdminStatCard';
import Badge from '../../components/common/Badge';
import { Button, Card, PageHeader, Select } from '@copilot/ui';
import { EmptyState } from '@copilot/ui';
import * as adminApi from '../../services/adminApi';
import { formatPlanPrice } from '../../services/pricingApi';

const PAGE_SIZE = 20;

// Only the outcomes that ever reach this page's tables - matches
// models/payment.py's PaymentStatus.
const PAYMENT_STATUS_BADGE = {
  captured: 'ok',
  refunded: 'warning',
  failed: 'danger',
  authorized: 'neutral',
};

// models/payment_subscription.py's RECOVERABLE_STATUSES - everything
// the orphan sweep could still act on.
const ORPHAN_STATUS_BADGE = {
  created: 'neutral',
  authenticated: 'neutral',
  active: 'ok',
  pending: 'warning',
  halted: 'danger',
};

function offsetToPage(offset, limit) {
  return Math.floor(offset / limit) + 1;
}

function pageToOffset(page, limit) {
  return (page - 1) * limit;
}

// Converts a currency's own gross/refunded pair into the description
// line under its stat card, so "net" never stands alone without the
// two numbers it was computed from.
function currencyDescription(row) {
  if (row.refunded <= 0) {
    return `${row.payment_count} payment${row.payment_count === 1 ? '' : 's'}`;
  }
  return `${formatPlanPrice(row.gross, row.currency)} gross − ${formatPlanPrice(
    row.refunded,
    row.currency
  )} refunded`;
}

function RevenueSection() {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    adminApi
      .getAdminRevenueSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load revenue.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <div className="form-alert">{error}</div>;
  }

  const byCurrency = summary?.by_currency || [];
  const byPlan = summary?.by_plan || [];

  return (
    <>
      <div className="stat-cards admin-stat-cards">
        {isLoading ? (
          <AdminStatCard title="Net Revenue" value="" icon="receipt" loading />
        ) : byCurrency.length === 0 ? (
          <AdminStatCard
            title="Net Revenue"
            value={formatPlanPrice(0, 'INR')}
            icon="receipt"
            description="No captured payments yet."
          />
        ) : (
          byCurrency.map((row) => (
            <AdminStatCard
              key={row.currency}
              title={`Net Revenue (${row.currency})`}
              value={formatPlanPrice(row.net, row.currency)}
              icon="receipt"
              description={currencyDescription(row)}
            />
          ))
        )}
      </div>

      <Card interactive title="Revenue by plan" className="dashboard-main-card">
        {isLoading ? (
          <div className="stat-card-skeleton" />
        ) : byPlan.length === 0 ? (
          <EmptyState
            title="No plan revenue yet."
            description="Revenue appears here once a paid plan takes its first captured charge."
          />
        ) : (
          <AdminDataTable
            columns={[
              { key: 'plan_name', label: 'Plan' },
              { key: 'billing_cycle', label: 'Cycle' },
              { key: 'payment_count', label: 'Payments' },
              {
                key: 'gross',
                label: 'Gross',
                render: (row) => formatPlanPrice(row.gross, row.currency),
              },
              {
                key: 'refunded',
                label: 'Refunded',
                render: (row) => formatPlanPrice(row.refunded, row.currency),
              },
              {
                key: 'net',
                label: 'Net',
                render: (row) => formatPlanPrice(row.net, row.currency),
              },
            ]}
            rows={byPlan}
            getRowKey={(row) => `${row.plan_id}-${row.billing_cycle}-${row.currency}`}
            isLoading={false}
          />
        )}
      </Card>
    </>
  );
}

function PaymentsSection() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(1);
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');

    adminApi
      .getAdminPayments({
        status: status || undefined,
        limit: PAGE_SIZE,
        offset: pageToOffset(page, PAGE_SIZE),
      })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load payments.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [status, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card interactive title="Payments" className="dashboard-main-card">
      <div className="admin-toolbar">
        <div className="admin-toolbar-filter">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by payment status"
          >
            <option value="">All Statuses</option>
            <option value="captured">Captured</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
            <option value="authorized">Authorized</option>
          </Select>
        </div>
      </div>

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={[
              {
                key: 'created_at',
                label: 'Date',
                render: (p) => formatAdminDateTime(p.created_at),
              },
              {
                key: 'user',
                label: 'User',
                render: (p) => p.user_email || '—',
              },
              {
                key: 'plan_name',
                label: 'Plan',
                render: (p) =>
                  p.plan_name
                    ? `${p.plan_name}${p.billing_cycle ? ` (${p.billing_cycle})` : ''}`
                    : '—',
              },
              {
                key: 'amount',
                label: 'Amount',
                render: (p) => formatPlanPrice(p.amount, p.currency),
              },
              {
                key: 'amount_refunded',
                label: 'Refunded',
                render: (p) =>
                  p.amount_refunded > 0
                    ? formatPlanPrice(p.amount_refunded, p.currency)
                    : '—',
              },
              {
                key: 'status',
                label: 'Status',
                render: (p) => (
                  <Badge variant={PAYMENT_STATUS_BADGE[p.status] || 'neutral'}>
                    {p.status}
                  </Badge>
                ),
              },
              {
                key: 'provider_payment_id',
                label: 'Payment ID',
                render: (p) => (
                  <span className="admin-table-subtext">{p.provider_payment_id}</span>
                ),
              },
            ]}
            rows={items}
            getRowKey={(p) => p.id}
            isLoading={isLoading}
            loadingLabel="Loading payments..."
            emptyTitle="No payments found."
            emptyDescription="Try a different status filter."
          />

          {!isLoading && (
            <AdminPagination
              page={offsetToPage(pageToOffset(page, PAGE_SIZE), PAGE_SIZE)}
              totalPages={totalPages}
              total={total}
              itemLabel="payments"
              onPageChange={setPage}
              ariaLabel="Payments pages"
            />
          )}
        </>
      )}
    </Card>
  );
}

function OrphanedSubscriptionsSection() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSweeping, setIsSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);
  const [sweepError, setSweepError] = useState('');

  const load = useCallback(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');

    adminApi
      .getAdminOrphanedSubscriptions({
        limit: PAGE_SIZE,
        offset: pageToOffset(page, PAGE_SIZE),
      })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load orphaned subscriptions.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  useEffect(() => load(), [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const runSweep = async () => {
    setIsSweeping(true);
    setSweepError('');
    setSweepResult(null);

    try {
      // min_age_minutes: 0 - an admin running this on demand wants
      // every eligible row checked now, not only ones old enough for
      // the automatic interval sweep to have acted on already.
      const result = await adminApi.runAdminRecoverPendingGrants({
        min_age_minutes: 0,
      });
      setSweepResult(result);
      setPage(1);
      load();
    } catch (err) {
      setSweepError(err.message || 'Recovery sweep failed.');
    } finally {
      setIsSweeping(false);
    }
  };

  return (
    <Card interactive title="Orphaned subscriptions" className="dashboard-main-card">
      <p className="admin-section-description">
        Provider subscriptions Razorpay currently considers live or still chargeable
        that never produced an entitlement row here - most often a lost webhook. The
        automatic recovery sweep re-checks each one against the provider before
        granting anything; nothing below is trusted on its own.
      </p>

      <div className="admin-toolbar">
        <Button onClick={runSweep} disabled={isSweeping}>
          {isSweeping ? 'Running sweep...' : 'Run recovery sweep now'}
        </Button>
      </div>

      {sweepError && <div className="form-alert">{sweepError}</div>}
      {sweepResult && (
        <p className="admin-action-status success">
          Checked {sweepResult.checked}, recovered {sweepResult.recovered}, still
          pending {sweepResult.still_pending}, ended {sweepResult.ended}, errors{' '}
          {sweepResult.errors}.
        </p>
      )}

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={[
              {
                key: 'created_at',
                label: 'Since',
                render: (row) => formatAdminDateTime(row.created_at),
              },
              { key: 'user', label: 'User', render: (row) => row.user_email || '—' },
              { key: 'plan_name', label: 'Plan', render: (row) => row.plan_name || '—' },
              {
                key: 'status',
                label: 'Provider Status',
                render: (row) => (
                  <Badge variant={ORPHAN_STATUS_BADGE[row.status] || 'neutral'}>
                    {row.status}
                  </Badge>
                ),
              },
              {
                key: 'provider_subscription_id',
                label: 'Provider Subscription ID',
                render: (row) => (
                  <span className="admin-table-subtext">
                    {row.provider_subscription_id}
                  </span>
                ),
              },
            ]}
            rows={items}
            getRowKey={(row) => row.id}
            isLoading={isLoading}
            loadingLabel="Loading orphaned subscriptions..."
            emptyTitle="No orphaned subscriptions."
            emptyDescription="Every live provider subscription has a matching entitlement row."
          />

          {!isLoading && (
            <AdminPagination
              page={offsetToPage(pageToOffset(page, PAGE_SIZE), PAGE_SIZE)}
              totalPages={totalPages}
              total={total}
              itemLabel="rows"
              onPageChange={setPage}
              ariaLabel="Orphaned subscriptions pages"
            />
          )}
        </>
      )}
    </Card>
  );
}

function AdminBillingPage() {
  return (
    <div>
      <PageHeader
        title="Billing"
        description="Revenue, payments, and provider reconciliation across every user."
      />

      <RevenueSection />
      <PaymentsSection />
      <OrphanedSubscriptionsSection />
    </div>
  );
}

export default AdminBillingPage;
